import { cva, type VariantProps } from 'class-variance-authority'
import { LucideProps } from 'lucide-react'
import {
  cloneElement,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
  ReactNode
} from 'react'

import { cn } from '../../../utils/cn'

const BASE_CLASS_NAME =
  'flex items-center gap-x-2 relative bg-white rounded-md border border-gray-300 focus-within:border-blue-600 transition-colors duration-300 focus-within:ring-2 focus-within:ring-blue-300 focus-within:text-blue-600'

const ERROR_CLASS_NAME =
  '!border-red-600 focus-within:ring-red-200 !text-red-600'

const inputVariants = cva(BASE_CLASS_NAME, {
  variants: {
    inputSize: {
      xs: 'px-2 h-8',
      sm: 'px-3 h-9',
      base: 'px-3 h-10',
      l: 'px-4 h-12'
    }
  },
  defaultVariants: {
    inputSize: 'base'
  }
})

const inputIconVariants = cva('', {
  variants: {
    inputSize: {
      xs: 'size-4',
      sm: 'size-5',
      base: 'size-5',
      l: 'size-6'
    }
  },
  defaultVariants: {
    inputSize: 'base'
  }
})

interface InputWrapperProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren,
    VariantProps<typeof inputVariants> {
  rightActionSlot?: ReactNode
  leftIconSlot?: ReactElement<LucideProps>
  hasError?: boolean
}

export const InputWrapper: FC<InputWrapperProps> = ({
  children,
  className,
  rightActionSlot,
  leftIconSlot,
  inputSize,
  hasError
}) => {
  const leftIcon = leftIconSlot
    ? cloneElement(leftIconSlot, {
        'aria-hidden': 'true',
        className: cn(
          inputIconVariants({ inputSize }),
          leftIconSlot.props.className
        )
      })
    : null

  return (
    <div
      className={cn(
        inputVariants({ inputSize }),
        {
          [ERROR_CLASS_NAME]: hasError
        },
        className
      )}>
      {leftIcon}
      {children}
      {rightActionSlot}
    </div>
  )
}
